import useSEO from '@/hooks/useSEO';
import { spreadVsCommissionFAQs } from './data';
import ExecutionModelsSection from './components/ExecutionModelsSection';
import SideBySideSection from './components/SideBySideSection';
import BreakEvenSection from './components/BreakEvenSection';
import WhenEachWinsSection from './components/WhenEachWinsSection';
import RealTraderScenariosSection from './components/RealTraderScenariosSection';
import SpreadDynamicsSection from './components/SpreadDynamicsSection';
import SpreadVsCommissionFAQSection from './components/SpreadVsCommissionFAQSection';
import RelatedGuidesSection from './components/RelatedGuidesSection';

export default function SpreadVsCommission() {
  useSEO({
    /* @replace:svc-title */

    title: 'Spread vs Commission: Compare Forex Trading Costs',
    /* @replace:svc-description */

    description: 'Compare spread-only and raw-spread commission accounts using total-cost formulas, break-even examples and scenarios for different trading styles.',
    /* @replace:svc-keywords */

    keywords: 'spread vs commission, raw spread account, standard account, forex pricing model, forex trading costs',
    /* @replace:svc-canonical */

    canonical: '/spread-vs-commission/',
  });

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: spreadVsCommissionFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-foreground-950">
        {/* Layer 1 — top-down depth gradient, lifts the upper area where the title sits */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.04] via-transparent to-transparent" />

        {/* Layer 2 — primary glow, a soft halo directly behind the headline to draw the eye upward */}
        <div className="absolute top-[-260px] left-1/2 -translate-x-1/2 w-[1100px] h-[760px] rounded-full opacity-[0.16] blur-[170px] bg-primary-500" />
        {/* primary core — tighter, brighter center gives the glow real body instead of a flat wash */}
        <div className="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[520px] h-[420px] rounded-full opacity-[0.12] blur-[120px] bg-primary-400" />

        {/* Layer 3 — accent glow, offset low-left and sitting behind the comparison card to cradle it */}
        <div className="absolute bottom-[-220px] left-[14%] w-[720px] h-[620px] rounded-full opacity-[0.11] blur-[150px] bg-accent-500" />
        {/* faint counter-balance glow on the right for compositional symmetry */}
        <div className="absolute top-[34%] right-[-160px] w-[560px] h-[560px] rounded-full opacity-[0.06] blur-[150px] bg-accent-500" />

        {/* Layer 4 — radial vignette, darkens the corners so light pools toward the center and content pops */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 85% at 50% 32%, transparent 0%, transparent 45%, oklch(var(--foreground-950) / 0.55) 100%)',
          }}
        />

        {/* Layer 5 — bottom fade for a seamless transition into the next section */}
        <div className="absolute bottom-0 left-0 right-0 h-[160px] bg-gradient-to-b from-transparent to-foreground-950" />

        <div className="relative max-w-main mx-auto px-4 md:px-6 pt-20 md:pt-28 pb-16 md:pb-24 text-center">
          {/* Title */}
          <h1 className="text-[46px] md:text-[72px] font-[750] text-white leading-[1.06] tracking-[-0.015em] mb-6">
            Spread <span className="italic font-[700] text-accent-400">vs</span> Commission
          </h1>

          {/* Description */}
          <p className="text-[16px] md:text-[18px] text-foreground-300 leading-relaxed max-w-[600px] mx-auto mb-14 md:mb-18">
            Two pricing models, one question: which one leaves more money in your pocket? The answer depends entirely on how you trade.
          </p>

          {/* Comparison card */}
          <div className="max-w-[760px] mx-auto">
            <div className="rounded-2xl border border-white/[0.10] overflow-hidden">
              {/* Card header */}
              <div className="px-7 md:px-10 py-3.5 border-b border-white/[0.07] flex items-center justify-between">
                <span className="text-[12px] font-bold uppercase tracking-[0.15em] text-foreground-400">The Fundamental Trade-Off</span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-600">Side by Side</span>
              </div>

              {/* Two columns */}
              <div className="grid grid-cols-1 md:grid-cols-2">
                {/* Spread-Only */}
                <div className="p-7 md:p-10 border-b md:border-b-0 md:border-r border-white/[0.07] text-left">
                  <div className="flex items-center gap-2.5 mb-7">
                    <div className="w-2.5 h-2.5 rounded-full bg-primary-500 shadow-[0_0_10px_oklch(var(--primary-500)/0.5)]" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.10em] text-primary-400">Spread-Only</span>
                  </div>

                  <div className="flex items-baseline gap-2 mb-1.5">
                    <span className="text-[52px] md:text-[64px] font-[750] text-white leading-none tabular-nums">1.2</span>
                    <span className="text-[16px] font-semibold text-foreground-400">pips</span>
                  </div>
                  <div className="text-[14px] font-medium text-foreground-500 mb-6">average spread</div>

                  {/* Visual bar */}
                  <div className="h-[5px] bg-primary-500/15 rounded-full overflow-hidden mb-6">
                    <div className="h-full bg-gradient-to-r from-primary-500/40 to-primary-500/70 rounded-full" style={{ width: '100%' }} />
                  </div>

                  <p className="text-[14px] text-foreground-400 leading-[1.7]">
                    Wider spread with zero separate commission. One line item on your trade ticket — simple, but rarely the cheapest choice for active traders.
                  </p>
                </div>

                {/* ECN */}
                <div className="p-7 md:p-10 text-left">
                  <div className="flex items-center gap-2.5 mb-7">
                    <div className="w-2.5 h-2.5 rounded-full bg-accent-500 shadow-[0_0_10px_oklch(var(--accent-500)/0.5)]" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.10em] text-accent-400">ECN / Commission</span>
                  </div>

                  <div className="flex items-baseline gap-2 mb-1.5">
                    <span className="text-[52px] md:text-[64px] font-[750] text-white leading-none tabular-nums">0.2</span>
                    <span className="text-[16px] font-semibold text-foreground-400">pips</span>
                  </div>
                  <div className="text-[14px] font-medium text-foreground-500 mb-6">+ $7 round-turn</div>

                  {/* Visual bar */}
                  <div className="h-[5px] bg-accent-500/15 rounded-full overflow-hidden mb-6">
                    <div className="h-full bg-gradient-to-r from-accent-500/40 to-accent-500/70 rounded-full" style={{ width: '16.7%' }} />
                  </div>

                  <p className="text-[14px] text-foreground-400 leading-[1.7]">
                    Raw interbank spread plus transparent commission. Two visible costs, but the total is typically 25–40% lower at meaningful volume.
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom hint */}
            <div className="mt-7 text-center flex flex-col items-center gap-2">
              <span className="text-[13px] text-foreground-500">
                Scroll for side-by-side scenarios, break-even math, and real trader comparisons
              </span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-foreground-600 text-sm" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <ExecutionModelsSection />
      <SideBySideSection />
      <BreakEvenSection />
      <WhenEachWinsSection />
      <RealTraderScenariosSection />
      <SpreadDynamicsSection />
      <SpreadVsCommissionFAQSection />
      <RelatedGuidesSection />
    </div>
  );
}